import uuid from '../utils/uuidv4'

export const SCROLL_OBSERVER_EVENT = 'ScrollObserverEvent'
export const SCROLL_OBSERVER_SKIP_OBSERVE_ATTR = 'data-scroll-observer-skip'

/**
 * State classes for the ScrollObserver.
 * @typedef {Object} ScrollObserverCoreClasses
 *
 * @property {string} [aboveEnterViewportClass='has-target-leave-below-view'] - Class to apply when the tracked element enters the root to the top.
 * @property {string} [aboveLeaveViewportClass='has-target-inside-view'] - Class to apply when the tracked element leaves the root to the top.
 * @property {string} [belowEnterViewportClass='has-target-enter-below-view'] - Class to apply when the tracked element enters the root to the bottom.
 * @property {string} [belowLeaveViewportClass='has-target-outside-view'] - Class to apply when the tracked element leaves the root to the bottom.
 * @property {string} [inViewportClass='has-target-enter-above-view'] - Class to apply when the tracked element is inside root.
 * @property {string} [outsideViewportClass='has-target-leave-above-view'] - Class to apply when the tracked element is outside root.
 * @property {string} [mostVisibleViewportAttribute='data-target-most-visible-view'] - Attribute to apply to the most visible tracked element.
 */

/**
 * ScrollObserverCoreCallback
 * @typedef {Object} ScrollObserverCoreCallback
 * @param {HTMLElement} element
 * @param {object} opts
 * @param {function} onMatch
 * @returns {(function(*): void)|*}
 */

/**
 * The ScrollObserverCore type.
 * @typedef {Object} ScrollObserverCore
 * @property {null|number} lastScrollTop - Tracks last scroll top position.
 * @property {*} ___uuid - Internal identifier.
 * @property {null|HTMLElement} mostVisibleEntry - The most-visible entry.
 * @property {ScrollObserverCoreClasses} classes - State classes to apply.
 * @property {{allowMultiple: boolean, root: null, rootMargin: string, threshold: number}} scrollObserverOptions - The IntersectionObserver options.
 * @property {ScrollObserverCoreCallback} observeEntries - The method to call upon observing the entries.
 */

/**
 * The ScrollObserverCore handles all internal class/intersection calculations.
 * @type {ScrollObserverCore}
 */
export const ScrollObserverCore = Object.seal({
  ___uuid: uuid(),
  lastScrollTop: null,
  mostVisibleEntry: null,
  classes: {
    inViewportClass: 'has-target-inside-view',
    outsideViewportClass: 'has-target-outside-view',
    aboveEnterViewportClass: 'has-target-enter-above-view',
    aboveLeaveViewportClass: 'has-target-leave-above-view',
    belowEnterViewportClass: 'has-target-enter-below-view',
    belowLeaveViewportClass: 'has-target-leave-below-view',
    mostVisibleViewportAttribute: 'data-target-most-visible-view',
  },
  scrollObserverOptions: {
    allowMultiple: false,
    /**
     * Setting proper defaults if data attributes are nullish.
     * @see https://w3c.github.io/IntersectionObserver/#dictdef-intersectionobserverinit
     */
    root: null,
    rootMargin: '0px',
    threshold: 0,
  },
  observeEntries(element, opts, onMatch = () => {}) {
    return (entries) => {
      entries.forEach((entry) => {
        const {
          boundingClientRect: { top },
          intersectionRect: { height },
        } = entry
        // eslint-disable-next-line no-underscore-dangle,no-param-reassign
        entry.target.__intersectionHeight = height

        if (!element.hasAttribute(SCROLL_OBSERVER_SKIP_OBSERVE_ATTR)) {
          if (entry.isIntersecting) {
            element.classList.remove(opts.classes.outsideViewportClass)
            element.classList.add(opts.classes.inViewportClass)
            if (ScrollObserverCore.lastScrollTop > top) {
              element.classList.add(opts.classes.belowEnterViewportClass)
              element.classList.remove(opts.classes.belowLeaveViewportClass)
              element.classList.remove(opts.classes.aboveEnterViewportClass)
            } else {
              element.classList.add(opts.classes.aboveEnterViewportClass)
              element.classList.remove(opts.classes.aboveLeaveViewportClass)
              element.classList.remove(opts.classes.belowEnterViewportClass)
            }
          } else {
            element.classList.add(opts.classes.outsideViewportClass)
            element.classList.remove(opts.classes.inViewportClass)
            if (ScrollObserverCore.lastScrollTop > top) {
              element.classList.add(opts.classes.aboveLeaveViewportClass)
              element.classList.remove(opts.classes.aboveEnterViewportClass)
              element.classList.remove(opts.classes.belowEnterViewportClass)
            } else {
              element.classList.add(opts.classes.belowLeaveViewportClass)
              element.classList.remove(opts.classes.belowEnterViewportClass)
              element.classList.remove(opts.classes.aboveEnterViewportClass)
            }
          }
        }
        ScrollObserverCore.lastScrollTop = top
      })
      if (opts.intersectionObserverOpts.allowMultiple) {
        ScrollObserverCore.mostVisibleEntry = entries.reduce((prev, curr) => {
          // eslint-disable-next-line no-underscore-dangle
          return curr.target.__intersectionHeight > (prev?.target?.__intersectionHeight ?? 0) ? curr : prev
        }, null)

        if (ScrollObserverCore.mostVisibleEntry) {
          element.setAttribute(
            ScrollObserverCore.classes.mostVisibleViewportAttribute,
            `#${ScrollObserverCore.mostVisibleEntry?.target?.id}`
          )
        }
      }

      if (!element.hasAttribute(SCROLL_OBSERVER_SKIP_OBSERVE_ATTR)) {
        onMatch({
          intersectionObserverEntries: entries,
          mostVisibleEntry: ScrollObserverCore.mostVisibleEntry,
        })
      }
    }
  },
})
